import { z } from "zod";

export const modulesSettingsSchema = z.object({
  inventory: z.object({ enabled: z.boolean() }).default({ enabled: false }),
  planSales: z.object({ enabled: z.boolean() }).default({ enabled: true }),
  accessControl: z
    .object({
      enabled: z.boolean(),
      validationMethod: z
        .enum(["FACIAL", "QR_CODE", "ACCESS_CODE", "FINGERPRINT"])
        .default("QR_CODE")
        .catch("QR_CODE"),
    })
    .default({ enabled: true, validationMethod: "QR_CODE" }),
  foodSales: z.object({ enabled: z.boolean() }).default({ enabled: true }),
  merchandiseSales: z
    .object({ enabled: z.boolean() })
    .default({ enabled: true }),
  bookings: z.object({ enabled: z.boolean() }).default({ enabled: true }),
  trainers: z.object({ enabled: z.boolean() }).default({ enabled: true }),
  expenses: z.object({ enabled: z.boolean() }).default({ enabled: true }),
  metrics: z.object({ enabled: z.boolean() }).default({ enabled: true }),
  machinery: z.object({ enabled: z.boolean() }).default({ enabled: true }),
  onlinePayments: z
    .object({ enabled: z.boolean() })
    .default({ enabled: false }),
  productCatalog: z
    .object({ enabled: z.boolean() })
    .default({ enabled: false }),
  salesOrders: z
    .object({
      enabled: z.boolean(),
      factoryEmail: z.string().nullable().optional().default(null),
    })
    .default({ enabled: false, factoryEmail: null }),
  invoicing: z.object({ enabled: z.boolean() }).default({ enabled: false }),
  productionMetrics: z
    .object({ enabled: z.boolean() })
    .default({ enabled: false }),
  b2bCrm: z.object({ enabled: z.boolean() }).default({ enabled: false }),
  otherIncome: z.object({ enabled: z.boolean() }).default({ enabled: true }),
  emailCampaigns: z
    .object({ enabled: z.boolean() })
    .default({ enabled: false }),
  aiAssistant: z.object({ enabled: z.boolean() }).default({ enabled: false }),
  clientApp: z.object({ enabled: z.boolean() }).default({ enabled: true }),
  suppliers: z.object({ enabled: z.boolean() }).default({ enabled: false }),
  shiftManagement: z
    .object({ enabled: z.boolean() })
    .default({ enabled: false }),
});

export const clientFieldStatusSchema = z.enum([
  "MANDATORY",
  "OPTIONAL",
  "NOT_REQUIRED",
]);

export const clientFieldConfigSchema = z.object({
  status: clientFieldStatusSchema,
  label: z.string(),
});

export const businessTypeSchema = z.enum(["GYM", "B2B"]).catch("GYM");
export type BusinessType = z.infer<typeof businessTypeSchema>;

export const clientFieldsConfigSchema = z.object({
  birthDate: clientFieldConfigSchema,
  phone: clientFieldConfigSchema,
  rut: clientFieldConfigSchema,
  gender: clientFieldConfigSchema,
  address: clientFieldConfigSchema,
  emergencyContactName: clientFieldConfigSchema,
  emergencyContactPhone: clientFieldConfigSchema,
  lastName: clientFieldConfigSchema.optional(),
  email: clientFieldConfigSchema.optional(),
  businessName: clientFieldConfigSchema.default({
    status: "NOT_REQUIRED",
    label: "Razón Social",
  }),
  businessActivity: clientFieldConfigSchema.default({
    status: "NOT_REQUIRED",
    label: "Giro Comercial",
  }),
  website: clientFieldConfigSchema.default({
    status: "NOT_REQUIRED",
    label: "Sitio Web",
  }),
  businessSize: clientFieldConfigSchema.default({
    status: "NOT_REQUIRED",
    label: "Tamaño de Empresa",
  }),
  industry: clientFieldConfigSchema.default({
    status: "NOT_REQUIRED",
    label: "Industria",
  }),
});

export const currentGymSchema = z.object({
  publicId: z.string(),
  name: z.string(),
  rut: z.string().nullable(),
  legalName: z.string().nullable(),
  address: z.string().nullable(),
  country: z.string().nullable(),
  timezone: z.string().default("UTC"),
  logoKey: z.string().nullable(),
  logoKeyEmail: z.string().nullable().optional(),
  taxesConfig: z.object({
    vat: z.number(),
  }),
  modulesSettings: modulesSettingsSchema,
  clientFieldsConfig: clientFieldsConfigSchema,
  businessType: businessTypeSchema,
});

export type CurrentGym = z.infer<typeof currentGymSchema>;
export type ModulesSettings = z.infer<typeof modulesSettingsSchema>;
export type AccessControlValidationMethod =
  | "FACIAL"
  | "QR_CODE"
  | "ACCESS_CODE"
  | "FINGERPRINT";
export type ClientFieldStatus = z.infer<typeof clientFieldStatusSchema>;
export type ClientFieldConfig = z.infer<typeof clientFieldConfigSchema>;
export type ClientFieldsConfig = z.infer<typeof clientFieldsConfigSchema>;
